import React, { useEffect, useMemo, useState } from 'react';

import { DOCUMENT_TYPES } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import { useCanManageInvoiceStatus } from '~support-at-home/hooks/useCanManageInvoiceStatus';
import { useCanUnlockInvoiceStatus } from '~support-at-home/hooks/useCanUnlockInvoiceStatus';

import { BulkActions, Button, ClickableIcon, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view';
import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';
import EligibilityConfirmDialog from '~support-at-home/components/EligibilityConfirmDialog';

import { BulkInvoiceStatusSkipReason } from '~support-at-home/models';

import { columnsFactory } from '~support-at-home/forms/co-contributions/list';
import { searchFields } from '~support-at-home/forms/co-contributions/search';

import { useGetClientInvoiceStatusesQuery } from '~lookups/actions/clientInvoiceStatuses';
import { setTitle } from '~main/actions/login';
import {
  useBulkUpdateClientInvoiceStatusMutation,
  useGetClientInvoicesQuery,
} from '~support-at-home/actions/clientInvoices';

import CoContributionsWizard from './co-contributions-wizard';
import CoContributionsModal from './CoContributionsModal';

// TAV-2053: bulk-status target tags. The lookup table also carries `draft`,
// `failed`, `void`, `deleted`, but the AC only authorises paid/unpaid as
// bulk operations.
const STATUS_TAG_PAID = 'paid';
const STATUS_TAG_UNPAID = 'sent';

type BulkStatusAction = 'mark-paid' | 'mark-unpaid';

const BULK_ACTION_TO_TAG: Record<BulkStatusAction, string> = {
  'mark-paid': STATUS_TAG_PAID,
  'mark-unpaid': STATUS_TAG_UNPAID,
};

// Human-readable reasons the API gives for skipping a row in a bulk update,
// so the toast can explain why nothing changed rather than a bare count.
const SKIP_REASON_LABELS: Record<BulkInvoiceStatusSkipReason, string> = {
  gocardless_locked: 'managed by GoCardless',
  no_change: 'already in that status',
  invalid_transition: 'not a permitted status change',
  unlock_required: 'needs unlock permission',
};

const summariseSkips = (skipped: { reason: BulkInvoiceStatusSkipReason }[]): string => {
  const counts = new Map<BulkInvoiceStatusSkipReason, number>();
  for (const { reason } of skipped) counts.set(reason, (counts.get(reason) ?? 0) + 1);
  const parts = [...counts.entries()].map(([reason, count]) => `${count} ${SKIP_REASON_LABELS[reason] ?? reason}`);
  return `${skipped.length} skipped (${parts.join(', ')})`;
};

const CoContributions: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setParams] = useSearch('co-contributions-search', {});
  const search = (values: Record<string, any>) => setParams(values);

  const [wizardOpen, setWizardOpen] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);

  const { generateSeparate, generateCombined, emailSeparate } = useDocumentBatchGeneration();
  const { showToast } = useToast();
  const canManageStatus = useCanManageInvoiceStatus();
  // "Mark as Unpaid" reverts paid → sent, a reverse transition only
  // unlock-capable users may perform.
  const canUnlockStatus = useCanUnlockInvoiceStatus();

  const openContribModal = (id: string) => {
    setInvoiceModalId(id);
    setModalOpen(true);
  };

  useEffect(() => {
    if (isActive) {
      setTitle('Client Co-Contributions');
    }
  }, [isActive]);

  // A new search replaces the visible rows — a failed search empties the table
  // (see the `data` guard below) and a successful one may return a different
  // set, so any pending row selection must not carry over to rows that are
  // gone. `searchParams` is `useState`-backed (stable between submits), so this
  // fires only on an actual search, not on every render or a client-side page.
  useEffect(() => {
    setSelectedInvoiceIds([]);
  }, [searchParams]);

  const { data: statuses = [] } = useGetClientInvoiceStatusesQuery();
  const { data: invoices = [], isFetching, isError, error } = useGetClientInvoicesQuery(searchParams);
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useBulkUpdateClientInvoiceStatusMutation();

  // TAV-2053: pending bulk-status confirmation. `null` while no dialog is
  // open; otherwise tracks which target the user clicked so the
  // EligibilityConfirmDialog can render the right copy.
  const [pendingStatusAction, setPendingStatusAction] = useState<BulkStatusAction | null>(null);

  const statusTagToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const status of statuses) map.set(status.tag, status.id);
    return map;
  }, [statuses]);

  const selectedInvoices = useMemo(
    () => invoices.filter((invoice) => selectedInvoiceIds.includes(String(invoice.id))),
    [invoices, selectedInvoiceIds],
  );

  const bulkActionOptions = [
    { value: 'generate-separate', label: 'Generate PDFs' },
    { value: 'generate-combined', label: 'Generate Combined PDF', minimum: 2 },
    { value: 'email-separate', label: 'Email Client Invoices' },
    { value: 'generate-csv', label: 'Export to CSV' },
    ...(canManageStatus ? [{ value: 'mark-paid', label: 'Mark as Paid' }] : []),
    // Reverting to Unpaid (paid → sent) is an unlock-only reverse transition.
    ...(canManageStatus && canUnlockStatus ? [{ value: 'mark-unpaid', label: 'Mark as Unpaid' }] : []),
  ];

  const handleBulkAction = async (action: string) => {
    if (selectedInvoiceIds.length === 0) return;

    // TAV-2053: status changes need a confirmation dialog (eligibility split,
    // GoCardless-locked count). Defer the mutation until the user confirms.
    if (action === 'mark-paid' || action === 'mark-unpaid') {
      setPendingStatusAction(action);
      return;
    }

    try {
      switch (action) {
        case 'generate-separate':
          await generateSeparate(DOCUMENT_TYPES.CO_CONTRIBUTION.SINGLE, selectedInvoiceIds).unwrap();
          showToast('success', `Started generating ${selectedInvoiceIds.length} invoice PDF(s)`);
          break;
        case 'generate-combined':
          await generateCombined(DOCUMENT_TYPES.CO_CONTRIBUTION.COMBINED, selectedInvoiceIds).unwrap();
          showToast('success', 'Started generating combined invoice PDF');
          break;
        case 'email-separate':
          await emailSeparate(DOCUMENT_TYPES.CO_CONTRIBUTION.SINGLE, selectedInvoiceIds).unwrap();
          showToast('success', `Scheduled ${selectedInvoiceIds.length} invoice email(s)`);
          break;
        case 'generate-csv':
          await generateCombined(DOCUMENT_TYPES.CO_CONTRIBUTION.CSV, selectedInvoiceIds).unwrap();
          showToast('success', `Started generating CSV export for ${selectedInvoiceIds.length} invoices`);
          break;
        default:
          console.warn('Unknown bulk action:', action);
      }
      setSelectedInvoiceIds([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to process bulk action';
      showToast('danger', message);
      console.error('Bulk action failed:', error);
    }
  };

  const handleStatusConfirm = async (eligibleIds: string[]) => {
    if (!pendingStatusAction) return;
    const targetTag = BULK_ACTION_TO_TAG[pendingStatusAction];
    const targetStatusId = statusTagToId.get(targetTag);
    if (!targetStatusId) {
      showToast('danger', `Unable to resolve status "${targetTag}". Please refresh and try again.`);
      setPendingStatusAction(null);
      return;
    }

    try {
      const result = await bulkUpdateStatus({ invoice_ids: eligibleIds, status_id: targetStatusId }).unwrap();
      const targetLabel = pendingStatusAction === 'mark-paid' ? 'Paid' : 'Unpaid';
      const updatedCount = result.updated.length;
      const skippedSuffix = result.skipped.length > 0 ? ` - ${summariseSkips(result.skipped)}` : '';
      showToast(
        updatedCount > 0 ? 'success' : 'info',
        `Marked ${updatedCount} ${updatedCount === 1 ? 'invoice' : 'invoices'} as ${targetLabel}${skippedSuffix}`,
      );
      setSelectedInvoiceIds([]);
      setPendingStatusAction(null);
    } catch (error) {
      // RTK `unwrap()` rejects with the FetchBaseQueryError/SerializedError
      // shape, not an Error instance — `instanceof Error` would miss the API
      // message. `getErrorMessage` walks both shapes.
      showToast('danger', getErrorMessage(error));
      console.error('Bulk status update failed:', error);
    }
  };

  return (
    <div className="p-2 co-contributions">
      <Toolbar
        id="co-contributions"
        rightCluster={
          <>
            <BulkActions
              id="co-contributions-bulk-actions"
              selectedIds={selectedInvoiceIds}
              onAction={handleBulkAction}
              actionOptions={bulkActionOptions}
            />
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="co-contributions-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Button dataCy="co-contributions-new-button" size="sm" color="success" onClick={() => setWizardOpen(true)}>
              <Icon className="me-2" icon={faPlus} />
              New Invoice Batch
            </Button>
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields(statuses)}
        initialValues={searchParams}
        onSubmit={search}
      />
      {isError && (
        <ErrorResponse
          message={`Failed to load co-contributions - ${getErrorMessage(error)}`}
          dataCy="co-contributions-error"
        />
      )}
      <TableView
        id="co-contributions-table"
        columns={columnsFactory(openContribModal)}
        data={isError ? [] : invoices}
        isLoading={isFetching}
        // On error the ErrorResponse banner above is the sole message; an empty
        // string suppresses the "no results" text (an `undefined` here would
        // trip TableEmpty's "No data available" default and re-introduce a
        // contradictory message under the banner).
        emptyMessage={isError ? '' : 'No co-contributions found'}
        readonly
        striped
        canSelect
        selectedIds={selectedInvoiceIds}
        onRowSelectionChange={(invoiceIds) => setSelectedInvoiceIds((invoiceIds as (string | number)[]).map(String))}
      />
      <CoContributionsWizard isOpen={wizardOpen} setIsOpen={setWizardOpen} />
      <CoContributionsModal isOpen={modalOpen} setIsOpen={setModalOpen} clientId={invoiceModalId} />
      <EligibilityConfirmDialog
        invoices={selectedInvoices}
        targetStatusLabel={pendingStatusAction === 'mark-paid' ? 'Paid' : 'Unpaid'}
        targetStatusId={pendingStatusAction ? statusTagToId.get(BULK_ACTION_TO_TAG[pendingStatusAction]) : undefined}
        isReversal={pendingStatusAction === 'mark-unpaid'}
        isOpen={pendingStatusAction !== null}
        isSubmitting={isBulkUpdating}
        onClose={() => setPendingStatusAction(null)}
        onConfirm={handleStatusConfirm}
      />
    </div>
  );
};

export default CoContributions;
